import { inject, Injectable } from '@angular/core';
import { isArray, isObject } from 'lodash-es';
import { EMPTY } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import {
  AnalyticsCardType,
  BulkBudgetForecastSettingsInput,
  Currency,
  GqlService,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { Utils } from '@shared/utils/utils';

import { createInitialState, ForecastStore } from './forecast.store';

interface ActivityByDriver {
  cost: number;
  driver: 'N/A' | 'Patient' | 'Site' | 'Time';
  percentage: number;
}

interface UnforecastedCosts {
  activity_count: number;
  total_cost: number;
}

@Injectable({ providedIn: 'root' })
export class ForecastService {
  private eventService = inject(EventService);
  private forecastStore = inject(ForecastStore);
  private gqlService = inject(GqlService);
  private organizationQuery = inject(OrganizationQuery);
  private overlayService = inject(OverlayService);

  getActivitiesByDriver() {
    return this.organizationQuery.selectActive().pipe(
      switchMap((org) => {
        if (org) {
          const bv = this.organizationQuery.getPrimaryBudgetVersion(org.id);

          if (!bv?.manual_forecast) {
            return this.eventService.selectAnalyticsCard$({
              analytics_card_type: AnalyticsCardType.FORECAST_ACTIVITIES_BY_DRIVER,
              budget_version_id: bv?.budget_version_id,
              vendor_id: org.id,
            });
          }
        }

        return EMPTY;
      }),
      tap(({ data, errors, success }) => {
        if (success && data) {
          this.forecastStore.update({
            activitiesByDriver: this.parseActivitiesByDriverResponse(data.data),
          });
        } else {
          this.forecastStore.update({
            activitiesByDriver: createInitialState().activitiesByDriver,
          });

          if (errors?.length) {
            this.overlayService.error(errors);
          }
        }
      })
    );
  }

  getUnforecastedCosts() {
    return this.organizationQuery.selectActive().pipe(
      switchMap((organization) => {
        if (organization) {
          const budgetVersion = this.organizationQuery.getPrimaryBudgetVersion(organization.id);

          if (!budgetVersion?.manual_forecast) {
            return this.eventService.selectAnalyticsCard$({
              analytics_card_type: AnalyticsCardType.UNFORECASTED_ACTIVITIES_COST,
              budget_version_id: budgetVersion?.budget_version_id,
              vendor_id: organization.id,
            });
          }
        }

        return EMPTY;
      }),
      tap(({ data, errors, success }) => {
        if (success && data) {
          this.forecastStore.update({
            unforecastedCosts: this.parseUnforecastedCostsResponse(data.data),
          });
        } else {
          this.forecastStore.update({
            unforecastedCosts: createInitialState().unforecastedCosts,
          });

          if (errors?.length) {
            this.overlayService.error(errors);
          }
        }
      })
    );
  }

  parseActivitiesByDriverResponse(data: ActivityByDriver[] | string) {
    const arr = isArray(data) ? data : (JSON.parse(data) as ActivityByDriver[]);

    const response = createInitialState();

    arr.forEach(({ cost, driver, percentage }) => {
      if (
        Object.prototype.hasOwnProperty.call(response.activitiesByDriver, driver) &&
        driver !== 'N/A'
      ) {
        response.activitiesByDriver[driver] = {
          cost,
          costStr: Utils.currencyFormatter(cost, {
            currency: this.organizationQuery.getActive()?.currency || Currency.USD,
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
            notation: 'compact',
            style: 'currency',
          }),
          percentage,
        };
      }
    });

    return response.activitiesByDriver;
  }

  parseUnforecastedCostsResponse(data: string | UnforecastedCosts) {
    return (isObject(data) ? data : JSON.parse(data)).total_cost;
  }

  saveUpdateBudgetForecastSettings$(params: BulkBudgetForecastSettingsInput) {
    return this.gqlService.bulkUpdateBudgetForecastSettings$(params).pipe(
      tap(({ errors }) => {
        if (errors.length) {
          this.overlayService.error(errors);
        }
      })
    );
  }
}
